import { Component, DestroyRef, inject, Injector, Input, OnInit, Signal, computed } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CheckoutUpsellExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { GlobalSettingsFormShape } from '../../../../components/smart-cart-form-global-settings/global-settings.model';

/** Dots in the reorder drag-handle glyph (⠿). */
const DRAG_HANDLE_DOT_COUNT = 6;

/**
 * Appearance section of the Product Selection tab: product display type, navigation style, section
 * title, variant selector, the Button design block (per-widget colours that inherit from the global
 * Button Design until overridden), the vendor / product-type display texts, and the Design block.
 * Binds to the widget's `productSelection` FormGroup (`style` + `settings`).
 */
@Component({
  selector: 'smart-cart-upsell-appearance',
  templateUrl: './product-appearance.component.html',
  styleUrls: ['../product-selection.component.scss'],
  standalone: false,
})
export class SmartCartUpsellAppearanceComponent implements OnInit {
  @Input({ required: true }) formGroup!: UntypedFormGroup;
  /** Global Button Design settings — the per-widget button colors inherit these until overridden. */
  @Input({ required: true }) globalSettingsFormGroup!: FormGroup<GlobalSettingsFormShape>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly dragHandleDots = Array(DRAG_HANDLE_DOT_COUNT).fill(0);

  // Button design: each display control shows `widget ?? global`; user edits write to the widget
  // control (null = inherit global). The overridden signals drive the "Reset to global color" link.
  readonly displayButtonBackground = new FormControl<string>('', { nonNullable: true });
  readonly displayButtonText = new FormControl<string>('', { nonNullable: true });
  readonly displayButtonHover = new FormControl<string>('', { nonNullable: true });
  readonly displayButtonIcon = new FormControl<string>('', { nonNullable: true });
  readonly displayButtonCornerRadius = new FormControl<number>(0, { nonNullable: true });
  buttonBackgroundOverridden!: Signal<boolean>;
  buttonTextOverridden!: Signal<boolean>;
  buttonHoverOverridden!: Signal<boolean>;
  buttonIconOverridden!: Signal<boolean>;
  buttonCornerRadiusOverridden!: Signal<boolean>;

  readonly displayTypeOptions = [
    { text: 'Carousel', value: CheckoutUpsellExtension.DisplayType.CARDS, icon: 'carousel' },
    { text: 'List', value: CheckoutUpsellExtension.DisplayType.ROW, icon: 'two-rectangle' },
    { text: 'Compact', value: CheckoutUpsellExtension.DisplayType.COMPACT, icon: 'rectangle' },
  ];

  readonly carouselNavigationStyleOptions = [
    {
      value: CheckoutUpsellExtension.CarouselNavigationStyle.ARROWS,
      text: 'Arrows',
      description: 'Navigate between products using arrow buttons',
    },
    {
      value: CheckoutUpsellExtension.CarouselNavigationStyle.SCROLL,
      text: 'Scroll bar',
      description: 'Scroll horizontally through the product list with a draggable bar',
    },
  ];

  readonly variantSelectorOptions = [
    {
      value: 'inside',
      label: 'Always Show',
      helpText: 'Dropdown is always visible above or next to the main CTA button',
    },
    { value: 'outside', label: 'Popup', helpText: 'Dropdown appears when clicking the main CTA button' },
  ];

  readonly imageFitOptions = [
    { label: 'Square', value: 'square', helpText: 'Crops to a square thumbnail' },
    { label: 'Original', value: 'original', helpText: 'Shows the full image as uploaded on Shopify' },
  ];

  ngOnInit(): void {
    const global = this.globalSettingsFormGroup.controls.buttonDesign.controls;
    this.wireDisplayString(
      this.displayButtonBackground,
      this.settingsControl('buttonBackgroundColor'),
      global.backgroundColor,
    );
    this.wireDisplayString(this.displayButtonText, this.settingsControl('buttonTextColor'), global.textColor);
    this.wireDisplayString(this.displayButtonHover, this.settingsControl('buttonHoverColor'), global.hoverTextColor);
    this.wireDisplayString(this.displayButtonIcon, this.settingsControl('buttonIconColor'), global.iconColor);
    this.wireDisplayNumber(
      this.displayButtonCornerRadius,
      this.settingsControl('buttonCornerRadius'),
      global.cornerRadius,
    );

    this.buttonBackgroundOverridden = this.overriddenSignal(this.settingsControl('buttonBackgroundColor'));
    this.buttonTextOverridden = this.overriddenSignal(this.settingsControl('buttonTextColor'));
    this.buttonHoverOverridden = this.overriddenSignal(this.settingsControl('buttonHoverColor'));
    this.buttonIconOverridden = this.overriddenSignal(this.settingsControl('buttonIconColor'));
    this.buttonCornerRadiusOverridden = this.overriddenSignal(this.settingsControl('buttonCornerRadius'));
  }

  /** Reverts a per-widget button-design override back to `null` so it inherits the global value again. */
  resetToGlobal(control: AbstractControl | null): void {
    control?.setValue(null);
  }

  private settingsControl(name: string): FormControl {
    return this.formGroup.get(['settings', name]) as FormControl;
  }

  private wireDisplayString(display: FormControl<string>, widget: FormControl, global: AbstractControl): void {
    const syncDown = (): void => display.setValue(widget.value ?? (global.value as string), { emitEvent: false });
    syncDown();
    widget.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    global.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    display.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((next) => {
      const value = next || null;
      if (widget.value !== value) widget.setValue(value);
    });
  }

  private wireDisplayNumber(display: FormControl<number>, widget: FormControl, global: AbstractControl): void {
    const syncDown = (): void => display.setValue(widget.value ?? (global.value as number), { emitEvent: false });
    syncDown();
    widget.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    global.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    display.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((next) => {
      if (widget.value !== next) widget.setValue(next);
    });
  }

  private overriddenSignal(widget: AbstractControl): Signal<boolean> {
    const value = toSignal(widget.valueChanges, { initialValue: widget.value, injector: this.injector });
    return computed(() => value() !== null);
  }

  setDisplayType(value: string): void {
    this.formGroup.get('style.displayType')?.setValue(value);
    this.formGroup.markAsDirty();
  }

  // ── Additional display texts (Display vendor / product type) — reorderable, like the extension ──
  get additionalDisplayTextsArray(): UntypedFormArray {
    return this.formGroup.get('settings.additionalDisplayTexts') as UntypedFormArray;
  }

  asGroup(control: AbstractControl): UntypedFormGroup {
    return control as UntypedFormGroup;
  }

  labelForType(type: string): string {
    return type === 'product-type' ? 'Product Type' : 'Vendor';
  }

  dropDisplayText(event: CdkDragDrop<unknown[]>): void {
    // Reorder through the FormArray API (removeAt/insert) rather than mutating `.controls` directly,
    // so form state and validity stay in sync.
    const displayTexts = this.additionalDisplayTextsArray;
    const control = displayTexts.at(event.previousIndex);
    displayTexts.removeAt(event.previousIndex);
    displayTexts.insert(event.currentIndex, control);
    displayTexts.controls.forEach((ctrl, index) => ctrl.get('priority')?.setValue(index));
    displayTexts.updateValueAndValidity();
    this.formGroup.markAsDirty();
  }
}
